import React from "react";
import { BoardCard as BoardCardType } from "containers/project-boards/types";
import { boardGradient } from "containers/project-boards/board-color";
import { StarIcon, PinIcon } from "./icons";

interface BoardCardProps {
  board: BoardCardType;
  onOpen: (id: string) => void;
  onToggleStar: (board: BoardCardType) => void;
  onTogglePin: (board: BoardCardType) => void;
}

// The pin and star show only on hover, which a touch screen does not have: there they are always
// drawn and big enough for a finger.
const TOUCH_ICON = "[@media(hover:none)]:p-3.5 [@media(hover:none)]:opacity-100";

function BoardCard({ board, onOpen, onToggleStar, onTogglePin }: BoardCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(board.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(board.id);
        }
      }}
      className="group relative flex h-[104px] cursor-pointer flex-col overflow-hidden rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="relative flex-1" style={{ background: boardGradient(board) }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(board);
          }}
          aria-label={board.isPinned ? "Unpin board" : "Pin board"}
          title={board.isPinned ? "Unpin" : "Pin"}
          className={`absolute left-1.5 top-1.5 rounded-md p-1 text-white transition hover:bg-black/30 ${TOUCH_ICON} ${
            board.isPinned ? "bg-black/25" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <PinIcon filled={board.isPinned} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar(board);
          }}
          aria-label={board.isStarred ? "Unstar board" : "Star board"}
          title={board.isStarred ? "Unstar" : "Star"}
          className={`absolute right-1.5 top-1.5 rounded-md p-1 text-white transition hover:scale-110 ${TOUCH_ICON} ${
            board.isStarred ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <StarIcon filled={board.isStarred} />
        </button>
      </div>
      <div className="px-2.5 py-2">
        <span className="block truncate text-[13px] font-semibold text-[var(--p-color-text)]">
          {board.name}
        </span>
      </div>
    </div>
  );
}

export default React.memo(BoardCard);
